import { GenesisBusinessBrief, GenesisResults, GenesisMetadata } from '../types/genesisBusinessBrief';
import { BusinessInfoPayload, MarketInfoPayload } from '../services/genesisApi';

/**
 * Réponse brute de l'API Genesis
 */
export interface GenesisRawResponse {
    id: string;
    user_id: number;
    session_id: string;
    status: string;
    market_research: { status: string; data: any };
    content_generation: { status: string; data: any };
    logo_creation: { status: string; data: any };
    seo_optimization: { status: string; data: any };
    template_selection: { status: string; data: any };
    overall_confidence: number;
    is_ready_for_website: boolean;
    generated_at: string;
    tokens_used: number;
}

/**
 * Mappe la réponse Genesis vers le format GenesisBusinessBrief du frontend.
 */
export function mapGenesisResponseToBrief(
    genesisResponse: GenesisRawResponse,
    businessInfo: BusinessInfoPayload,
    marketInfo: MarketInfoPayload
): GenesisBusinessBrief {

    const marketData = genesisResponse.market_research?.data || {};
    const contentData = genesisResponse.content_generation?.data || {};
    const logoData = genesisResponse.logo_creation?.data || {};
    const seoData = genesisResponse.seo_optimization?.data || {};
    const templateData = genesisResponse.template_selection?.data || {};

    // Construire les résultats
    const results: GenesisResults = {
        research: {
            market_analysis: {
                market_size: marketData.market_size || "Analyse en cours...",
                trends: marketData.trends || [],
                target_demographics: marketData.demographics || {
                    age_range: "N/A",
                    income_level: "N/A",
                    tech_adoption: "N/A"
                }
            },
            competitors: (marketData.competitors || []).map((c: any) => ({
                name: c.name || "Concurrent",
                strengths: c.strengths || "",
                weaknesses: c.weaknesses || "",
                market_position: c.position || ""
            })),
            opportunities: (marketData.opportunities || []).map((o: any) => ({
                description: o.description || "",
                potential: o.potential || "",
                timeline: o.timeline || ""
            }))
        },
        content: {
            homepage: {
                fr: {
                    hero_title: contentData.homepage?.hero_title || businessInfo.company_name,
                    hero_subtitle: contentData.homepage?.hero_subtitle || businessInfo.description,
                    cta_primary: contentData.homepage?.cta || "Commencer",
                    value_propositions: contentData.homepage?.value_props || []
                }
            },
            about: {
                fr: {
                    story: contentData.about?.story || "",
                    mission_statement: contentData.about?.mission || "",
                    values: contentData.about?.values || []
                }
            },
            services: {
                fr: {
                    list: (contentData.services || []).map((s: any) => ({
                        name: s.name || "",
                        description: s.description || "",
                        pricing: s.pricing || ""
                    }))
                }
            },
            contact: {
                fr: {
                    address: contentData.contact?.address || "",
                    phone: contentData.contact?.phone || "",
                    email: contentData.contact?.email || "",
                    hours: contentData.contact?.hours || ""
                }
            }
        },
        logo: {
            url: logoData.url || "",
            style: logoData.style || "modern",
            colors: {
                primary: logoData.colors?.primary || "#667eea",
                secondary: logoData.colors?.secondary || "#764ba2",
                accent: logoData.colors?.accent || "#ffffff"
            }
        },
        seo: {
            meta_title: seoData.meta_title || businessInfo.company_name,
            meta_description: seoData.meta_description || businessInfo.description,
            keywords: seoData.keywords || [],
            og_image: seoData.og_image || ""
        },
        template: {
            id: templateData.id || "default",
            name: templateData.name || "Template Standard",
            category: templateData.category || "business",
            features: templateData.features || []
        }
    };

    // Construire les métadonnées
    const metadata: GenesisMetadata = {
        confidence_score: genesisResponse.overall_confidence || 0.8,
        ready_for_website: genesisResponse.is_ready_for_website || false,
        generation_time_seconds: genesisResponse.tokens_used ? Math.ceil(genesisResponse.tokens_used / 100) : 30,
        providers_used: {
            search: "perplexity",
            llm: "openai",
            image: "dall-e"
        },
        languages_available: ["fr"],
        successful_agents: ["market_research", "content_generation"],
        failed_agents: []
    };

    // Construire le brief complet
    return {
        brief_id: genesisResponse.id,
        user_id: genesisResponse.user_id,
        coaching_session_id: genesisResponse.session_id,
        business_name: businessInfo.company_name,
        vision: businessInfo.description,
        mission: marketInfo.goals.join(", "),
        target_audience: marketInfo.target_audience,
        differentiation: marketInfo.market_challenges,
        sector: businessInfo.industry,
        location: {
            city: "Non spécifié",
            country: "Côte d'Ivoire",
            region: "Afrique de l'Ouest"
        },
        results,
        metadata,
        timestamps: {
            created_at: genesisResponse.generated_at,
            updated_at: genesisResponse.generated_at,
            expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // +30 jours
        }
    };
}
